import { CommonModule } from '@angular/common';
import {
  Component,
  HostListener,
  OnDestroy,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { RoleService } from '@app/shared/services/roles.service';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { EzGridComponent } from '@template-ui/components/ez-grid/ez-grid.component';
import {
  ButtonsCellRenderer,
  type EzButtonCellParams,
} from '@template-ui/components/ez-grid/cell-renderers/buttons-cell-renderer/buttons-cell-renderer.component';
import type {
  ColDef,
  GridOptions,
  ICellRendererParams,
} from 'ag-grid-community';
import { Observable, Subscription } from 'rxjs';
import {
  mapWorkflowRegistryDiagnosticError,
  type WorkflowRegistryDiagnosticDisplayError,
} from '../workflow-registry-diagnostics/platform-workflow-registry-diagnostics.models';
import { PlatformOperationsService } from './platform-operations.service';
import { PlatformOperationsStateService } from './platform-operations-state.service';
import { RecurringSchedulesComponent } from './recurring-schedules/recurring-schedules.component';
import { HearingEnrollmentSetupComponent } from '../workflow-registry-diagnostics/hearing-enrollment-setup.component';
import { RecurringScheduleStateService } from './recurring-schedules/recurring-schedule-state.service';
import {
  deliveryIdentity,
  type OutboxDeliveryDetail,
  type OutboxMonitorItem,
  type OutboxMonitorPage,
  type OutboxMonitorStatus,
  type OutboxSummary,
  type OutboxWorkerState,
  type OutboxWorkerAudit,
} from './platform-operations.models';

type ReadSlot = 'summary' | 'worker' | 'workerHistory' | 'page' | 'detail';
@Component({
  selector: 'phms-platform-operations',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    EzGridComponent,
    RecurringSchedulesComponent,
    HearingEnrollmentSetupComponent,
  ],
  templateUrl: './platform-operations.component.html',
  styleUrl: './platform-operations.component.scss',
})
export class PlatformOperationsComponent implements OnDestroy {
  deploymentConfigurationLoaded = false;
  openDeploymentConfiguration(event: Event): void {
    if ((event.target as HTMLDetailsElement).open)
      this.deploymentConfigurationLoaded = true;
  }
  private readonly api = inject(PlatformOperationsService);
  private readonly roles = inject(RoleService);
  private readonly operationState = inject(PlatformOperationsStateService);
  private readonly scheduleState = inject(RecurringScheduleStateService);
  private readonly route = inject(ActivatedRoute, { optional: true });
  readonly canRead = this.roles.hasAnyPermissionSignal(
    'platform_access_admin.read',
  );
  readonly canAuthor = this.roles.hasAnyPermissionSignal(
    'platform_access_admin.author',
  );
  readonly statuses: { code: OutboxMonitorStatus; label: string }[] = [
    { code: 'dead', label: 'Failed · needs recovery' },
    { code: 'pending', label: 'Pending' },
    { code: 'leased', label: 'In progress / leased' },
    { code: 'completed', label: 'Completed' },
    { code: 'all', label: 'All delivery states' },
  ];
  status: OutboxMonitorStatus = 'dead';
  summary: OutboxSummary | null = null;
  worker: OutboxWorkerState | null = null;
  workerHistory: OutboxWorkerAudit[] = [];
  historyLoaded = false;
  rows: OutboxMonitorItem[] = [];
  page: OutboxMonitorPage | null = null;
  detail: OutboxDeliveryDetail | null = null;
  selectedIdentity = '';
  private selectedReference: { eventId: string; consumerId: string } | null =
    null;
  private readonly reasons = new Map<string, string>();
  get retryReason(): string {
    return this.reasons.get(this.selectedIdentity) ?? '';
  }
  set retryReason(value: string) {
    this.reasons.set(this.selectedIdentity, value);
  }
  workerReason = '';
  loading: Record<ReadSlot, boolean> = {
    summary: false,
    worker: false,
    workerHistory: false,
    page: false,
    detail: false,
  };
  errors: Partial<Record<ReadSlot, WorkflowRegistryDiagnosticDisplayError>> =
    {};
  actionError: WorkflowRegistryDiagnosticDisplayError | null = null;
  actionNotice = '';
  validationError = '';
  busy = false;
  accessDenied = false;
  staleDelivery = false;
  staleWorker = false;
  private cursor: string | undefined;
  private cursorHistory: (string | undefined)[] = [];
  private readonly readRequests = new Map<ReadSlot, Subscription>();
  private readonly readVersions = new Map<ReadSlot, number>();
  private mutationRequest?: Subscription;
  private mutationIntent: { fingerprint: string; key: string } | null = null;
  readonly columns: ColDef<OutboxMonitorItem>[] = [
    {
      colId: 'inspect',
      headerName: 'Delivery',
      minWidth: 115,
      width: 125,
      sortable: false,
      filter: false,
      cellRenderer: ButtonsCellRenderer,
      cellRendererParams: ({
        data,
      }: ICellRendererParams<OutboxMonitorItem>): EzButtonCellParams => ({
        buttonsConfig: [
          {
            id: 'inspect',
            buttonText: 'Inspect',
            severity: 'secondary',
            text: true,
            size: 'small',
          },
        ],
        onButtonClick: () => {
          if (data) this.selectDelivery(data);
        },
      }),
    },
    {
      field: 'eventType',
      headerName: 'Operation / event',
      minWidth: 200,
      flex: 1.5,
    },
    { field: 'consumerId', headerName: 'Consumer', minWidth: 190, flex: 1 },
    { field: 'status', headerName: 'State', width: 115 },
    { field: 'totalAttempts', headerName: 'Total attempts', width: 140 },
    {
      field: 'lastIssueCode',
      headerName: 'Last issue',
      minWidth: 175,
      flex: 1,
    },
    {
      field: 'nextAttemptUtc',
      headerName: 'Next attempt (UTC)',
      minWidth: 210,
      flex: 1,
    },
  ];
  readonly gridOptions: GridOptions<OutboxMonitorItem> = {
    domLayout: 'autoHeight',
    getRowId: ({ data }) => deliveryIdentity(data),
    onRowClicked: ({ data }) => {
      if (data) this.selectDelivery(data);
    },
    suppressCellFocus: false,
  };
  constructor() {
    effect(() => {
      const allowed = this.canRead();
      untracked(() => {
        if (allowed) {
          const eventId = this.route?.snapshot.queryParamMap?.get('eventId');
          const consumerId =
            this.route?.snapshot.queryParamMap?.get('consumerId');
          if (eventId && consumerId) {
            this.selectedReference = { eventId, consumerId };
            this.selectedIdentity = deliveryIdentity(this.selectedReference);
          }
          this.refresh();
        } else this.clearProtectedData();
      });
    });
  }
  ngOnDestroy(): void {
    this.cancelAllReads();
    this.mutationRequest?.unsubscribe();
    this.operationState.setOperationInProgress(false);
  }
  @HostListener('window:beforeunload', ['$event']) beforeUnload(
    event: BeforeUnloadEvent,
  ): void {
    if (this.busy) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
  get selected(): OutboxMonitorItem | null {
    return this.detail?.delivery ?? null;
  }
  get canRetry(): boolean {
    return (
      !!this.selected?.canRetry &&
      this.selected.status === 'dead' &&
      this.canRead() &&
      this.canAuthor() &&
      !this.accessDenied &&
      !this.busy &&
      !this.scheduleState.isOperationInProgress() &&
      !this.loading.detail &&
      !this.staleDelivery
    );
  }
  get canChangeWorker(): boolean {
    return (
      !!this.worker &&
      this.canRead() &&
      this.canAuthor() &&
      !this.accessDenied &&
      !this.busy &&
      !this.scheduleState.isOperationInProgress() &&
      !this.loading.worker &&
      !this.staleWorker
    );
  }
  get canPreviousPage(): boolean {
    return this.cursorHistory.length > 0;
  }
  identity(item: Pick<OutboxMonitorItem, 'eventId' | 'consumerId'>): string {
    return deliveryIdentity(item);
  }
  refresh(): void {
    if (!this.canRead() || this.busy) return;
    this.accessDenied = false;
    this.loadSummary();
    this.loadWorker();
    this.loadPage();
    if (this.selectedReference) this.loadDetail(this.selectedReference);
  }
  loadSummary(): void {
    if (!this.canRead() || this.busy || this.accessDenied) return;
    this.summary = null;
    this.read('summary', this.api.getSummary(), (summary) => {
      if (
        !summary ||
        ![
          summary.pending,
          summary.leased,
          summary.dead,
          summary.completed,
          summary.totalAttempts,
          summary.eventsWithoutConsumers,
        ].every((value) => Number.isSafeInteger(value) && value >= 0)
      )
        throw new Error('Invalid summary contract');
      this.summary = summary;
    });
  }
  loadWorker(): void {
    if (!this.canRead() || this.busy || this.accessDenied) return;
    this.worker = null;
    this.read('worker', this.api.getWorker(), (worker) => {
      if (!validWorker(worker)) throw new Error('Invalid worker contract');
      this.worker = worker;
      this.staleWorker = false;
    });
  }
  loadWorkerHistory(): void {
    if (!this.canRead() || this.busy || this.accessDenied) return;
    this.workerHistory = [];
    this.historyLoaded = false;
    this.read('workerHistory', this.api.getWorkerHistory(), (rows) => {
      if (
        !Array.isArray(rows) ||
        rows.length > 100 ||
        rows.some(
          (row) =>
            row.workerId !== 'event-outbox' ||
            !['pause', 'resume'].includes(row.action),
        )
      )
        throw new Error('Invalid worker history');
      this.workerHistory = rows;
      this.historyLoaded = true;
    });
  }
  changeStatus(): void {
    if (this.busy) return;
    this.cursor = undefined;
    this.cursorHistory = [];
    this.loadPage();
  }
  loadPage(): void {
    if (!this.canRead() || this.busy || this.accessDenied) return;
    const status = this.status,
      cursor = this.cursor;
    this.rows = [];
    this.page = null;
    this.read('page', this.api.getMonitor(status, cursor), (page) => {
      if (
        page?.contractVersion !== 1 ||
        !Array.isArray(page.items) ||
        page.items.length > 50 ||
        !page.items.every(validItem) ||
        new Set(page.items.map(deliveryIdentity)).size !== page.items.length ||
        page.hasMore !== !!page.nextCursor ||
        page.items.some((item) => status !== 'all' && item.status !== status)
      )
        throw new Error('Invalid monitor page contract');
      this.page = page;
      this.rows = page.items;
    });
  }
  nextPage(): void {
    if (this.busy || this.loading.page || !this.page?.nextCursor) return;
    this.cursorHistory.push(this.cursor);
    this.cursor = this.page.nextCursor;
    this.loadPage();
  }
  previousPage(): void {
    if (this.busy || this.loading.page || !this.canPreviousPage) return;
    this.cursor = this.cursorHistory.pop();
    this.loadPage();
  }
  selectDelivery(item: OutboxMonitorItem): void {
    if (
      this.busy ||
      !this.canRead() ||
      this.accessDenied ||
      !this.rows.some((row) => deliveryIdentity(row) === deliveryIdentity(item))
    )
      return;
    this.selectedIdentity = deliveryIdentity(item);
    this.selectedReference = {
      eventId: item.eventId,
      consumerId: item.consumerId,
    };
    this.actionError = null;
    this.actionNotice = '';
    this.validationError = '';
    this.staleDelivery = false;
    this.loadDetail(this.selectedReference);
  }
  refreshDetail(): void {
    if (this.selectedReference) this.loadDetail(this.selectedReference);
  }
  openDelivery(reference: { eventId: string; consumerId: string }): void {
    if (
      this.busy ||
      !this.canRead() ||
      this.accessDenied ||
      !reference.eventId ||
      !reference.consumerId
    )
      return;
    this.selectedReference = reference;
    this.selectedIdentity = deliveryIdentity(reference);
    this.actionError = null;
    this.actionNotice = '';
    this.validationError = '';
    this.staleDelivery = false;
    this.loadDetail(reference);
  }
  private loadDetail(reference: { eventId: string; consumerId: string }): void {
    if (!this.canRead() || this.busy || this.accessDenied) return;
    this.detail = null;
    this.read(
      'detail',
      this.api.getDelivery(reference.eventId, reference.consumerId),
      (detail) => {
        if (
          detail?.contractVersion !== 1 ||
          !validItem(detail.delivery) ||
          deliveryIdentity(detail.delivery) !== deliveryIdentity(reference) ||
          !Array.isArray(detail.attempts) ||
          !Array.isArray(detail.recoveries) ||
          detail.newEvaluation?.supported !== false ||
          !detail.newEvaluation.reasonCode ||
          !Number.isSafeInteger(detail.legacyAttemptCount) ||
          detail.legacyAttemptCount < 0
        )
          throw new Error('Invalid delivery detail contract');
        this.detail = detail;
        this.staleDelivery = false;
      },
    );
  }
  retryInvocation(): void {
    const delivery = this.selected;
    if (!delivery || !this.canRetry) return;
    const reason = this.retryReason.trim();
    if (!validReason(reason)) {
      this.validationError = 'Enter a retry reason of up to 1000 characters.';
      return;
    }
    const body = { reason, expectedTotalAttempts: delivery.totalAttempts };
    this.startMutation();
    this.mutationRequest = this.api
      .retryInvocation(
        delivery.eventId,
        delivery.consumerId,
        delivery.eTag,
        body,
        this.writeIntent(
          'retry',
          body,
          delivery.eTag,
          deliveryIdentity(delivery),
        ),
      )
      .subscribe({
        next: () => {
          this.finishMutation();
          this.mutationIntent = null;
          this.actionNotice =
            'Retry recorded for the same committed invocation. A later worker attempt will keep its original event and consumer identity; no new evaluation was started.';
          this.refresh();
        },
        error: (error) => this.mutationFailed(error, 'delivery'),
      });
  }
  changeWorker(paused: boolean): void {
    const worker = this.worker;
    if (!worker || !this.canChangeWorker || worker.paused === paused) return;
    const reason = this.workerReason.trim();
    if (!validReason(reason)) {
      this.validationError =
        'Enter a worker-control reason of up to 1000 characters.';
      return;
    }
    const body = { expectedRevision: worker.revision, paused, reason };
    this.startMutation();
    this.mutationRequest = this.api
      .updateWorker(
        worker.eTag,
        body,
        this.writeIntent('worker', body, worker.eTag, worker.workerId),
      )
      .subscribe({
        next: (updated) => {
          this.finishMutation();
          if (!this.canRead()) {
            this.clearProtectedData();
            return;
          }
          if (
            !validWorker(updated) ||
            updated.revision <= worker.revision ||
            updated.eTag === worker.eTag ||
            updated.paused !== paused ||
            updated.installationDispatchEnabled !==
              worker.installationDispatchEnabled
          ) {
            this.staleWorker = true;
            this.actionError = {
              title: 'Worker source mismatch',
              messages: [
                'The control receipt does not match the expected worker revision. Refresh worker state before another command. Your reason is kept.',
              ],
            };
            return;
          }
          this.worker = updated;
          this.workerHistory = [];
          this.historyLoaded = false;
          this.mutationIntent = null;
          this.actionNotice = paused
            ? 'Worker pause recorded. No new deliveries will be claimed; committed pending work remains inspectable.'
            : updated.effectiveDispatchEnabled
              ? 'Worker resume recorded. Pending committed work can continue under the installed dispatch configuration.'
              : 'Operator pause cleared. Dispatch remains disabled by installation configuration; no work is enabled.';
        },
        error: (error) => this.mutationFailed(error, 'worker'),
      });
  }
  private startMutation(): void {
    this.busy = true;
    this.operationState.setOperationInProgress(true);
    this.actionError = null;
    this.validationError = '';
    this.actionNotice = '';
  }
  private finishMutation(): void {
    this.busy = false;
    this.operationState.setOperationInProgress(false);
  }
  private mutationFailed(error: unknown, source: 'delivery' | 'worker'): void {
    this.finishMutation();
    this.actionError = mapError(error);
    if ([409, 412].includes(this.actionError.statusCode ?? 0)) {
      if (source === 'delivery') this.staleDelivery = true;
      else this.staleWorker = true;
    }
    if ([401, 403].includes(this.actionError.statusCode ?? 0)) {
      this.accessDenied = true;
      this.clearProtectedData();
    }
  }
  private writeIntent(
    action: string,
    body: unknown,
    eTag: string,
    identity: string,
  ): string {
    const fingerprint = JSON.stringify([action, identity, eTag, body]);
    if (this.mutationIntent?.fingerprint !== fingerprint)
      this.mutationIntent = {
        fingerprint,
        key:
          globalThis.crypto?.randomUUID?.() ??
          `outbox-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      };
    return this.mutationIntent.key;
  }
  private read<T>(
    slot: ReadSlot,
    request: Observable<T>,
    accept: (result: T) => void,
  ): void {
    this.cancelRead(slot);
    const version = this.readVersions.get(slot)!;
    this.loading[slot] = true;
    delete this.errors[slot];
    this.readRequests.set(
      slot,
      request.subscribe({
        next: (result) => {
          if (this.readVersions.get(slot) !== version || !this.canRead())
            return;
          this.loading[slot] = false;
          try {
            accept(result);
          } catch {
            this.errors[slot] = {
              title: 'Monitoring contract unavailable',
              messages: [
                'The response does not match this monitoring selection. Refresh this section before taking an action.',
              ],
            };
          }
        },
        error: (error) => {
          if (this.readVersions.get(slot) !== version) return;
          this.loading[slot] = false;
          const display = mapError(error);
          this.errors[slot] = display;
          if ([401, 403].includes(display.statusCode ?? 0)) {
            this.accessDenied = true;
            this.clearProtectedData();
          }
        },
      }),
    );
  }
  private cancelRead(slot: ReadSlot): void {
    this.readRequests.get(slot)?.unsubscribe();
    this.readVersions.set(slot, (this.readVersions.get(slot) ?? 0) + 1);
    this.loading[slot] = false;
  }
  private cancelAllReads(): void {
    (
      ['summary', 'worker', 'workerHistory', 'page', 'detail'] as ReadSlot[]
    ).forEach((slot) => this.cancelRead(slot));
  }
  private clearProtectedData(): void {
    this.cancelAllReads();
    this.summary = null;
    this.worker = null;
    this.workerHistory = [];
    this.historyLoaded = false;
    this.rows = [];
    this.page = null;
    this.detail = null;
  }
}
function validReason(reason: string): boolean {
  return reason.length > 0 && reason.length <= 1000;
}
function validItem(item: OutboxMonitorItem): boolean {
  return (
    !!item?.eventId &&
    !!item.consumerId &&
    !!item.eTag &&
    ['pending', 'leased', 'dead', 'completed'].includes(item.status) &&
    Number.isSafeInteger(item.totalAttempts) &&
    item.totalAttempts >= 0 &&
    Number.isSafeInteger(item.attemptCount) &&
    item.attemptCount >= 0
  );
}
function validWorker(worker: OutboxWorkerState): boolean {
  return (
    worker?.workerId === 'event-outbox' &&
    !!worker.eTag &&
    Number.isSafeInteger(worker.revision) &&
    worker.revision >= 0 &&
    typeof worker.paused === 'boolean' &&
    typeof worker.installationDispatchEnabled === 'boolean' &&
    worker.effectiveDispatchEnabled ===
      (worker.installationDispatchEnabled && !worker.paused)
  );
}
function mapError(error: unknown): WorkflowRegistryDiagnosticDisplayError {
  const result = mapWorkflowRegistryDiagnosticError(error);
  return result.statusCode === undefined
    ? {
        ...result,
        messages: [
          'Monitoring could not be loaded. Retry this section or contact support.',
        ],
      }
    : result;
}
